﻿import React, { useCallback, useEffect, useState } from 'react';
import { api, ApiClientRow, YandexCompanyProfile, YandexUserRow } from '../services/api';
import { useCabinetLang } from '../i18n';

type SettingsTab = 'users' | 'keys';

export const SettingsPage: React.FC = () => {
  const { t } = useCabinetLang();
  const companyStatusLabel = (status?: string) => {
    if (status === 'inactive') return t.companyInactive;
    if (status === 'suspended') return t.companySuspended;
    return t.companyActive;
  };
  const [tab, setTab] = useState<SettingsTab>('users');
  const [company, setCompany] = useState<YandexCompanyProfile | null>(null);
  const [users, setUsers] = useState<YandexUserRow[]>([]);
  const [clients, setClients] = useState<ApiClientRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [flash, setFlash] = useState('');

  const [showAddKey, setShowAddKey] = useState(false);
  const [keyName, setKeyName] = useState('Yandex API');
  const [newKey, setNewKey] = useState('');
  const [creatingKey, setCreatingKey] = useState(false);

  const [showAddUser, setShowAddUser] = useState(false);
  const [userName, setUserName] = useState('');
  const [userIin, setUserIin] = useState('');
  const [creatingUser, setCreatingUser] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [profile, u, c] = await Promise.all([
        api.getCompany(),
        api.listUsers(),
        api.listApiClients(),
      ]);
      setCompany(profile);
      setUsers(u);
      setClients(c);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.settingsLoadFailed);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const issueKey = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreatingKey(true);
    setFlash('');
    setNewKey('');
    try {
      const created = await api.createApiClient(keyName.trim() || 'Yandex API');
      setNewKey(created.apiKey);
      setShowAddKey(false);
      setKeyName('Yandex API');
      setFlash(t.keyCreated);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.keyCreateFailed);
    } finally {
      setCreatingKey(false);
    }
  };

  const revokeKey = async (id: string) => {
    if (!window.confirm(t.revokeConfirm)) return;
    try {
      await api.revokeApiClient(id);
      setFlash(t.keyRevoked);
      setNewKey('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.keyRevokeFailed);
    }
  };

  const addUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreatingUser(true);
    setFlash('');
    try {
      await api.createUser({
        name: userName.trim(),
        iin: userIin.trim(),
      });
      setUserName('');
      setUserIin('');
      setShowAddUser(false);
      setFlash(t.userAdded);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.userAddFailed);
    } finally {
      setCreatingUser(false);
    }
  };

  return (
    <div className="settings-section">
      <div className="section-header">
        <h2>{company?.name || t.settings}</h2>
      </div>
      {flash && <div className="settings-flash">{flash}</div>}
      {error && <div className="settings-error">{error}</div>}
      {loading && !company ? (
        <div className="no-documents">{t.loading}</div>
      ) : (
        <>
          <div className="settings-card">
            <div className="section-header" style={{ marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>{t.companyData}</h3>
            </div>
            <dl className="sa-dl">
              <div>
                <dt>{t.name}</dt>
                <dd>{company?.name || '—'}</dd>
              </div>
              <div>
                <dt>{t.bin}</dt>
                <dd>{company?.bin || '—'}</dd>
              </div>
              <div>
                <dt>{t.address}</dt>
                <dd>{company?.address || '—'}</dd>
              </div>
              <div>
                <dt>{t.status}</dt>
                <dd>{companyStatusLabel(company?.status)}</dd>
              </div>
            </dl>
          </div>

          <div className="sa-tabs">
            <button
              type="button"
              className={tab === 'users' ? 'btn-apply' : 'btn-reset'}
              onClick={() => setTab('users')}
            >
              {t.users}
            </button>
            <button
              type="button"
              className={tab === 'keys' ? 'btn-apply' : 'btn-reset'}
              onClick={() => setTab('keys')}
            >
              {t.apiKeys}
            </button>
          </div>

          {tab === 'users' && (
            <div className="settings-card">
              <div className="section-header" style={{ marginBottom: 12 }}>
                <h3 style={{ margin: 0 }}>{t.users}</h3>
                <button type="button" className="btn-apply" onClick={() => setShowAddUser(true)}>
                  {t.add}
                </button>
              </div>
              {showAddUser && (
                <form className="settings-form" onSubmit={(e) => void addUser(e)} style={{ marginBottom: 16 }}>
                  <p className="settings-hint">{t.userHint}</p>
                  <label>
                    {t.fullName}
                    <input
                      value={userName}
                      onChange={(e) => setUserName(e.target.value)}
                      required
                      placeholder={t.fullName}
                    />
                  </label>
                  <label>
                    {t.iin}
                    <input
                      value={userIin}
                      onChange={(e) => setUserIin(e.target.value.replace(/\D/g, '').slice(0, 12))}
                      required
                      inputMode="numeric"
                      maxLength={12}
                      placeholder={t.iin12}
                    />
                  </label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      type="submit"
                      className="btn-apply"
                      disabled={creatingUser || userIin.length !== 12}
                    >
                      {creatingUser ? t.saving : t.save}
                    </button>
                    <button
                      type="button"
                      className="btn-reset"
                      onClick={() => {
                        setShowAddUser(false);
                        setUserName('');
                        setUserIin('');
                      }}
                    >
                      {t.cancel}
                    </button>
                  </div>
                </form>
              )}
              {users.length === 0 && !showAddUser && (
                <div className="no-documents">{t.noUsers}</div>
              )}
              {users.length > 0 && (
                <div className="table-wrapper">
                <table className="documents-table users-table">
                  <thead>
                    <tr>
                      <th>{t.fullName}</th>
                      <th>{t.iin}</th>
                      <th>{t.status}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id}>
                        <td>{u.name}</td>
                        <td>{u.iin || '—'}</td>
                        <td>{u.is_active === false ? t.userDisabled : t.userActive}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )}
            </div>
          )}

          {tab === 'keys' && (
            <div className="settings-card">
              <div className="section-header" style={{ marginBottom: 12 }}>
                <h3 style={{ margin: 0 }}>{t.apiKeys}</h3>
                <button type="button" className="btn-apply" onClick={() => setShowAddKey(true)}>
                  {t.add}
                </button>
              </div>
              {newKey && (
                <div className="settings-secret" style={{ marginBottom: 16 }}>
                  <div className="settings-secret-label">{t.copyNow}</div>
                  <code>{newKey}</code>
                  <button
                    type="button"
                    className="btn-reset"
                    onClick={() => void navigator.clipboard.writeText(newKey)}
                  >
                    {t.copy}
                  </button>
                </div>
              )}
              {showAddKey && (
                <form className="settings-form" onSubmit={(e) => void issueKey(e)} style={{ marginBottom: 16 }}>
                  <label>
                    {t.keyName}
                    <input
                      value={keyName}
                      onChange={(e) => setKeyName(e.target.value)}
                      placeholder="Yandex API"
                      required
                    />
                  </label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="submit" className="btn-apply" disabled={creatingKey}>
                      {creatingKey ? t.creating : t.save}
                    </button>
                    <button
                      type="button"
                      className="btn-reset"
                      onClick={() => {
                        setShowAddKey(false);
                        setKeyName('Yandex API');
                      }}
                    >
                      {t.cancel}
                    </button>
                  </div>
                </form>
              )}
              {clients.length === 0 && !showAddKey && (
                <div className="no-documents">{t.noKeys}</div>
              )}
              {clients.length > 0 && (
                <div className="table-wrapper keys-table-wrap">
                <table className="documents-table keys-table">
                  <thead>
                    <tr>
                      <th>{t.name}</th>
                      <th>{t.prefix}</th>
                      <th>{t.status}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {clients.map((c) => (
                      <tr key={c.id}>
                        <td>{c.name}</td>
                        <td>
                          <code className="key-prefix">{c.keyPrefix}…</code>
                        </td>
                        <td>{c.isActive !== false ? t.userActive : t.revoked}</td>
                        <td>
                          {c.isActive !== false && (
                            <button type="button" className="btn-reset" onClick={() => void revokeKey(c.id)}>
                              {t.revoke}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};
